// components/LocaleSwitcher.tsx
"use client";

import * as React from "react";
import Link from "next/link";
import {usePathname} from "next/navigation";
import {availableLocales, i18n, type Locale} from "@/i18n-config";

/**
 * LocaleSwitcher
 * - Drop-in language switcher meant to live in your navbar.
 * - Keeps the trigger styled exactly like your existing Login button by letting you pass the same component or classes.
 *
 * Usage (same classes as your Login button):
 *   <LocaleSwitcher buttonClassName="px-4 py-2 rounded-md bg-primary text-primary-foreground" />
 *
 * Usage (if you have a <Link> component):
 *   <LocaleSwitcher LinkComponent={Link} linkProps={{ variant: "default", size: "sm" }} />
 */
type LinkLikeProps = React.ComponentPropsWithoutRef<typeof Link>;
type LinkLike = React.ComponentType<LinkLikeProps>;

interface Props {
  LinkComponent?: LinkLike; // e.g., next/link
  linkProps?: Record<string, any>; // forwarded to your LinkComponent
  buttonClassName?: string; // use same classes as your Login button
  listClassName?: string; // optional dropdown container classes
  itemClassName?: string; // optional item classes
  compact?: boolean; // trigger label: "EN ▼" (default)
}

export default function LocaleSwitcher({LinkComponent, linkProps, buttonClassName = "", listClassName = "", itemClassName = "", compact = true}: Props) {
  const pathname = usePathname();
  const [open, setOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const currentLocale: Locale | undefined = React.useMemo(() => {
    if (!pathname) return undefined;
    const parts = pathname.split("/");
    const maybe = parts[1] as Locale | undefined;
    return i18n.locales.includes(maybe as Locale) ? (maybe as Locale) : undefined;
  }, [pathname]);

  const redirectedPathname = (locale: Locale) => {
    if (!pathname) return `/${locale}`;
    const segments = pathname.split("/");
    const hasLocale = i18n.locales.includes(segments[1] as Locale);
    if (hasLocale) segments[1] = locale;
    else segments.splice(1, 0, locale);
    const next = segments.join("/").replace(/\/+/, "/");
    return next || `/${locale}`;
  };

  React.useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") setOpen(false);
  };

  const Trigger: LinkLike = LinkComponent ?? Link;
  const triggerLabel = compact ? (currentLocale ?? i18n.defaultLocale).toUpperCase() : "Language";

  return (
    <div className="relative" ref={menuRef} onKeyDown={onKeyDown}>
      <Trigger
        href="#"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="locale-switcher-menu"
        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
          e.preventDefault();
          setOpen((v) => !v);
        }}
        className={`${buttonClassName}`}
        {...(LinkComponent ? linkProps : {})}>
        {triggerLabel}
      </Trigger>

      {open && (
        <div id="locale-switcher-menu" role="menu" className={["absolute right-0 z-50 mt-2 min-w-[8rem] overflow-hidden rounded-lg bg-gray-900 shadow-lg focus:outline-none", listClassName || ""].join(" ")}>
          <ul className="py-1">
            {availableLocales.map((locale) => {
              const active = locale.code === currentLocale;
              return (
                <li key={locale.code} role="none">
                  <Link
                    role="menuitem"
                    href={redirectedPathname(locale.code)}
                    onClick={() => setOpen(false)}
                    className={[
                      "block px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-white transition-colors duration-200 focus:outline-none",
                      active ? "opacity-60 pointer-events-none" : "",
                      itemClassName || ""
                    ].join(" ")}
                    aria-current={active ? "page" : undefined}>
                    {locale.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
